import { COUNTRY_PAYMENT_PROVIDER, CURRENCY_PAYMENT_PROVIDER, DEFAULT_PAYMENT_PROVIDER, } from './constants.js';
export function slugify(input) {
    return input
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}
export function cartTotal(items) {
    return items.reduce((sum, i) => sum + i.price * i.quantity, 0);
}
/**
 * Derive the itemised order total from a cart and the store's billing rules.
 * Pure and deterministic so the API (the real charge + the persisted breakdown)
 * and the storefront (the order summary the shopper sees) always agree.
 *
 * Delivery applies only to physical lines. Stores with configured delivery
 * locations require an exact location match; stores without locations use the
 * flat fee. Inclusive tax is displayed but not added a second time. Blocking
 * currency/delivery problems and minimum-order failures are returned for the
 * caller to surface before payment.
 */
export function computeBilling(cart, billing, opts = {}) {
    const subtotal = round2(cartTotal(cart));
    const cartCurrencies = [...new Set(cart.map((item) => item.currency))];
    const currency = cartCurrencies[0] ?? billing?.currency ?? 'USD';
    const lines = [];
    const issues = [];
    const requiresDelivery = cart.some((item) => (item.fulfillmentType ?? 'physical') === 'physical');
    if (cartCurrencies.length > 1) {
        issues.push({
            code: 'mixed_currency',
            message: 'All items in a cart must use the same currency.',
        });
    }
    if (billing?.currency && cartCurrencies.length > 0 && billing.currency !== currency) {
        issues.push({
            code: 'billing_currency_mismatch',
            message: `This store's checkout charges are configured in ${billing.currency}, but the cart uses ${currency}.`,
        });
    }
    if (!billing) {
        return {
            currency,
            subtotal,
            deliveryFee: 0,
            taxAmount: 0,
            feesTotal: 0,
            total: subtotal,
            lines,
            ...(issues.length ? { issues } : {}),
        };
    }
    // Delivery applies only to physical cart lines. When configured locations
    // exist, checkout must select an exact one: never silently fall back.
    let deliveryFee = 0;
    if (billing.delivery?.enabled && requiresDelivery) {
        const locations = billing.delivery.locations ?? [];
        const match = billing.delivery.locations?.find((l) => l.slug === opts.deliveryLocationSlug);
        if (locations.length > 0 && !opts.deliveryLocationSlug) {
            issues.push({
                code: 'delivery_location_required',
                message: 'Choose a delivery location to continue.',
            });
        }
        else if (locations.length > 0 && !match) {
            issues.push({
                code: 'invalid_delivery_location',
                message: 'This delivery location is not available.',
            });
        }
        const base = locations.length > 0 ? (match?.fee ?? 0) : billing.delivery.flatFee;
        const threshold = billing.delivery.freeThreshold ?? 0;
        deliveryFee = threshold > 0 && subtotal >= threshold ? 0 : round2(base);
        if (locations.length === 0 || match) {
            lines.push({
                key: 'delivery',
                label: match ? `Delivery (${match.location})` : 'Delivery',
                amount: deliveryFee,
            });
        }
    }
    // Inclusive tax remains visible but is not added twice.
    let taxAmount = 0;
    if (billing.tax?.enabled && billing.tax.ratePercent > 0) {
        const taxable = subtotal + deliveryFee;
        taxAmount = billing.tax.inclusive
            ? round2((taxable * billing.tax.ratePercent) / (100 + billing.tax.ratePercent))
            : round2((taxable * billing.tax.ratePercent) / 100);
        lines.push({
            key: 'tax',
            label: `${billing.tax.label} (${billing.tax.ratePercent}%${billing.tax.inclusive ? ', included' : ''})`,
            amount: taxAmount,
        });
    }
    // Named fees (packaging, handling, …).
    let feesTotal = 0;
    for (const fee of billing.fees ?? []) {
        if (fee.amount <= 0)
            continue;
        feesTotal = round2(feesTotal + fee.amount);
        lines.push({ key: fee.id, label: fee.label, amount: round2(fee.amount) });
    }
    const total = round2(subtotal + deliveryFee + (billing.tax?.inclusive ? 0 : taxAmount) + feesTotal);
    const breakdown = {
        currency,
        subtotal,
        deliveryFee,
        taxAmount,
        feesTotal,
        total,
        lines,
        ...(issues.length ? { issues } : {}),
    };
    if (billing.minimumOrder && billing.minimumOrder > 0 && subtotal < billing.minimumOrder) {
        breakdown.belowMinimum = { minimum: billing.minimumOrder };
    }
    return breakdown;
}
const CURRENCY_SYMBOLS = {
    USD: '$',
    GBP: '£',
    EUR: '€',
    NGN: '₦',
    GHS: 'GH₵',
    KES: 'KSh',
    ZAR: 'R',
};
export function formatMoney(amount, currency) {
    const symbol = CURRENCY_SYMBOLS[currency] ?? `${currency} `;
    // Group thousands (₦45,000 not ₦45000) and drop the trailing ".00" on whole
    // amounts, which are the norm for NGN/GHS/KES/ZAR. Fractional amounts keep
    // two decimals so prices like $49.90 never render as "$49.9". Pin the locale
    // to 'en-US' so the grouping is identical regardless of server locale.
    const formatted = amount.toLocaleString('en-US', {
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2,
    });
    return `${symbol}${formatted}`;
}
export function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
/**
 * Resolve which platform payment provider a buyer should be charged through,
 * based on their region. Country wins; currency is the fallback; otherwise the
 * platform default. Pure function — shared by the API (real checkout) and the
 * storefront (preview the method before paying).
 */
export function resolvePaymentProvider(opts) {
    const country = opts.country?.toUpperCase();
    const currency = opts.currency?.toUpperCase();
    if (country && COUNTRY_PAYMENT_PROVIDER[country]) {
        return COUNTRY_PAYMENT_PROVIDER[country];
    }
    if (currency && CURRENCY_PAYMENT_PROVIDER[currency]) {
        return CURRENCY_PAYMENT_PROVIDER[currency];
    }
    return DEFAULT_PAYMENT_PROVIDER;
}
const PROVIDER_LABELS = {
    stripe: 'Card (Stripe)',
    paystack: 'Paystack',
    flutterwave: 'Flutterwave',
};
export function paymentProviderLabel(provider) {
    return PROVIDER_LABELS[provider] ?? provider;
}
/** Legacy — use calculateFeeBreakdown instead. */
export function splitOrderAmount(total) {
    const fee = Math.round(total * 0.05 * 100) / 100;
    const net = Math.round((total - fee) * 100) / 100;
    return { fee, net };
}
export function round2(n) {
    return Math.round(n * 100) / 100;
}
export function calculateCatiqMarkup(orderAmount, markupType, markupValue) {
    if (markupType === 'fixed') {
        return round2(markupValue);
    }
    return round2(orderAmount * (markupValue / 100));
}
export function estimatePsspFee(orderAmount, rate, flatFee) {
    return round2(orderAmount * rate + flatFee);
}
export function calculateTotalCharge(baseAmount, payerMode, estimatedPsspFee, catiqMarkup) {
    if (payerMode === 'merchant_absorbs') {
        return {
            totalCharge: baseAmount,
            merchantNet: round2(baseAmount - estimatedPsspFee - catiqMarkup),
        };
    }
    const paymentFee = round2(estimatedPsspFee + catiqMarkup);
    return {
        totalCharge: round2(baseAmount + paymentFee),
        merchantNet: baseAmount,
    };
}
export function calculateSettlementAmounts(actualPsspFee, totalCharge, catiqMarkup, payerMode, baseAmount) {
    const actualPlatformCut = round2(actualPsspFee + catiqMarkup);
    if (payerMode === 'merchant_absorbs') {
        return {
            merchantNet: round2(baseAmount - actualPsspFee - catiqMarkup),
            reconciliationVariance: 0,
        };
    }
    return {
        merchantNet: baseAmount,
        reconciliationVariance: 0,
    };
}
//# sourceMappingURL=utils.js.map